import { useState } from "react";
import { useParams } from "react-router-dom";
import { PatientAllergyDto, patientAllergyFormData } from "../types/patientAllergy";
import { patientAllergyService } from "../services/patientAllergyService";
import { useApi } from "./useApi";
import { useMutation } from "./useMutation";

export const usePatientAllergies = (overridePatientId?: string) => {
  const { patientId: urlPatientId } = useParams<{ patientId: string }>();
  const patientId = overridePatientId ?? urlPatientId;

  // ΕΝΑ ΚΑΙ ΜΟΝΑΔΙΚΟ request: Επιλέγει αυτόματα endpoint ανάλογα με το αν υπάρχει patientId
  const {
    data: allergies = [],
    loading: isFetching,
    error: getError,
    refetch,
    invalidateCache,
  } = useApi<PatientAllergyDto[]>(
    patientId ? `patient-allergies-${patientId}` : "patient-allergies-all",
    async (signal) => {
      if (patientId) {
        return patientAllergyService.getByPatientId(patientId, signal);
      }
      return patientAllergyService.getAll({}, signal);
    },
    [patientId]
  );

  // Mutation για Create (προσθέτει αυτόματα το patientId από το URL στο payload)
  const {
    execute: createExecution,
    loading: isCreating,
    error: createError,
  } = useMutation((newPatientAllergy: patientAllergyFormData) => {
    const payload = patientId 
      ? { ...newPatientAllergy, patientId: Number(patientId) } 
      : newPatientAllergy;
      
    return patientAllergyService.create(payload as any);
  });

  // Mutation για Delete
  const {
    execute: deleteExecution,
    loading: isDeleting,
    error: deleteError,
  } = useMutation(async (ids: number[]) => {
    await Promise.all(ids.map((id) => patientAllergyService.delete(id)));
    return ids;
  });

  const {
    execute: updateExecution,
    loading: isUpdating,
    error: updateError,
  } = useMutation(async ({ id, data }: { id: number; data: any }) => {
    return patientAllergyService.update(id, data); // (Ή το αντίστοιχο service του ασθενή)
  });

  const [modalState, setModalState] = useState({
    isOpen: false,
    title: "",
    message: "",
    type: "success" as "success" | "error",
  });

  const closeModal = () => {
    setModalState((prev) => ({ ...prev, isOpen: false }));
  };

  const createPatientAllergy = async (newPatientAllergy: patientAllergyFormData): Promise<void> => {
    try {
      await createExecution(newPatientAllergy);
      invalidateCache();
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Η αποθήκευση ολοκληρώθηκε επιτυχώς!",
        type: "success",
      });
      refetch(true);
    } catch (err: any) {
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: err.message || "Σφάλμα δημιουργίας",
        type: "error",
      });
    }
  };

  const updatePatientAllergy = async (id: number, updatedData: patientAllergyFormData): Promise<void> => {
    try {
      await updateExecution({ id, data: updatedData });
      invalidateCache();
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Η ενημέρωση ολοκληρώθηκε επιτυχώς!",
        type: "success",
      });
      refetch(true);
    } catch (err: any) {
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: err.message || "Σφάλμα ενημέρωσης",
        type: "error",
      });
    }
  };
  const deletePatientAllergies = async (ids: number[]): Promise<void> => {
    try {
      await deleteExecution(ids);
      invalidateCache();
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Η διαγραφή ολοκληρώθηκε επιτυχώς!",
        type: "success",
      });
      refetch(true);
    } catch (err: any) {
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: err.message || "Σφάλμα κατά τη διαγραφή",
        type: "error",
      });
    }
  };

  return {
    patientAllergies: allergies, // Τα δεδομένα (είτε για έναν ασθενή, είτε όλα)
    allAllergies: allergies,    // Alias για συμβατότητα
    isLoading: isFetching || isCreating || isDeleting,
    error: getError || createError || deleteError,
    fetchPatientAllergies: () => refetch(true),
    fetchAllergies: () => refetch(true),
    createPatientAllergy,
    updatePatientAllergy,
    deletePatientAllergies,
    modalState,
    closeModal,
  };
};